/**
 * Groups the chat menu's threads by recency, and floats pinned ones above.
 *
 * Both surfaces (native drawer, web sidebar) render the same groups from this
 * one function. Before it, the menu was a flat list ordered by `lastMessageAt`
 * with title-only rows, so "yesterday" and "four months ago" sat against each
 * other with nothing to tell them apart, and finding an old conversation meant
 * scrolling past every newer one.
 */
import type { AgentThreadSummary } from '@tabbio/types';

export type ThreadGroupKey =
  | 'pinned'
  | 'today'
  | 'yesterday'
  | 'previous7Days'
  | 'previous30Days'
  | 'older';

export type ThreadGroup = {
  key: ThreadGroupKey;
  threads: AgentThreadSummary[];
};

/** i18n key for a group heading, under `chat.threadGroups`. */
export function threadGroupLabelKey(key: ThreadGroupKey): string {
  return `chat.threadGroups.${key}`;
}

function threadTime(thread: AgentThreadSummary): number {
  const value = thread.lastMessageAt ?? thread.updatedAt ?? thread.createdAt;
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

/**
 * Buckets are measured from the start of the local day, not from `now` minus a
 * fixed number of hours. A conversation from 11pm last night belongs under
 * "Yesterday" all through today, rather than sliding into "Today" once it is
 * less than 24 hours old.
 */
export function groupThreadsByRecency(
  threads: readonly AgentThreadSummary[],
  now: Date = new Date(),
): ThreadGroup[] {
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const DAY = 86_400_000;
  const startOfYesterday = startOfToday - DAY;
  const start7Days = startOfToday - 7 * DAY;
  const start30Days = startOfToday - 30 * DAY;

  const buckets: Record<ThreadGroupKey, AgentThreadSummary[]> = {
    pinned: [],
    today: [],
    yesterday: [],
    previous7Days: [],
    previous30Days: [],
    older: [],
  };

  const sorted = [...threads].sort((a, b) => threadTime(b) - threadTime(a));

  for (const thread of sorted) {
    if (thread.pinned) {
      buckets.pinned.push(thread);
      continue;
    }

    const time = threadTime(thread);
    if (time >= startOfToday) buckets.today.push(thread);
    else if (time >= startOfYesterday) buckets.yesterday.push(thread);
    else if (time >= start7Days) buckets.previous7Days.push(thread);
    else if (time >= start30Days) buckets.previous30Days.push(thread);
    else buckets.older.push(thread);
  }

  return (Object.keys(buckets) as ThreadGroupKey[])
    .map((key) => ({ key, threads: buckets[key] }))
    .filter((group) => group.threads.length > 0);
}

/**
 * The menu shows a thread's title. An untitled thread still needs a row the user
 * can aim at, so callers pass their localized fallback rather than rendering an
 * empty line.
 */
export function threadDisplayTitle(
  thread: AgentThreadSummary,
  fallback: string,
): string {
  const title = thread.title?.trim();
  return title && title.length > 0 ? title : fallback;
}

/** Case-insensitive title search, matching what the menu's field implies. */
export function filterThreadsByQuery(
  threads: readonly AgentThreadSummary[],
  query: string,
): AgentThreadSummary[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...threads];
  return threads.filter((thread) =>
    (thread.title ?? '').toLowerCase().includes(needle),
  );
}
